import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from "clsx";
import { withStyles, Chip, Input, FormControl, Divider, Typography } from "@material-ui/core";
import guid from '../../../utils/guid';
import { themeTypeToBool, notArrayOrEmpty } from '../../../utils/helpers';

const useStyles = theme => ({
    field: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        cursor: "text",
        alignItems: "center"
    },
    container: {
        margin: theme.spacing(1),
        marginTop: theme.spacing(2)
    },
    chips: {
        display: "flex",
        flexWrap: "wrap"
    },
    chip: {
        margin: theme.spacing(0.3),
    },
    underline: {
        width: "100%"
    },
    divider: {
        background: themeTypeToBool(theme.palette.type) ? "#000" : "#FFF",
        height: 1,
        opacity: 0.8,
    },
    dividerFocused: {
        background: "#A3A0FB",
        height: 3,
        opacity: 0.9,
        boxShadow: "2 0"
    },
    label: {
        position: "absolute",
        top: 5,
        left: 0,
        transform: "translate(0, 0) scale(1)",
        transition: "color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms ,transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms"
    },
    labelFocused: {
        color: theme.palette.primary.main
    },
    labelTransformed: {
        transform: "translate(0, -18px) scale(0.75)",
        transformOrigin: "top left",
    }
});

const MultipleElementsInput = props => {
    const { classes, onChange, elements, inputValidation, label, autoFocus } = props;
    const [value, setValue] = useState("");
    const [chips, setChips] = useState([]);
    const [focus, setFocus] = useState(autoFocus);
    const input = useRef(null);

    useEffect(() => {
        if(!notArrayOrEmpty(elements)) {
            setChips(elements);
        }
    }, [elements]);

    useEffect(() => {
        if (notArrayOrEmpty(chips))
            return onChange && onChange([]);
        onChange && onChange(chips);
    }, [chips]);

    const printChips = (data) => {
        if (!Array.isArray(data) || data.length === 0) {
            return null;
        } else {
            return data.map(item => {
                return (
                  <Chip
                    key={guid()}
                    size="small"
                    className={classes.chip}
                    label={item}
                    onDelete={handleDelete.bind(this, item)}
                  />
                );
            });
        }
    };

    const handleDelete = (item) => setChips(elements => elements.filter(element => element !== item));

    const handleKey = (e) => {
        if (value.length === 0 && e.key === "Backspace") {
            if(!notArrayOrEmpty(chips))
                handleDelete(chips[chips.length - 1]);
            setValue("");
        }
        else if (e.key === "Enter" && inputValidation(value)) {
            if (!chips.includes(value))
                setChips(data => [...data, value]);
            setValue("");
        }
    };

    const handleChange = (e) => {
        let newValue = e.target.value;
        newValue = newValue.trimStart();
        let dividePattern = /(;|\s)/;
        if (!dividePattern.test(newValue))
            return setValue(newValue);
        var newElement = newValue.split(dividePattern)[0];
        if (inputValidation(newElement)) {
            if (!chips.includes(newElement))
                setChips(data => [...data, newElement]);
            return setValue("");
        }
        setValue(e.target.value);
    };

    const handleFormClick = () => input.current.focus();

    const handleBlur = () => setFocus(false);

    return (
        <div className={classes.container}>
            <FormControl
                className={classes.field}
                fullWidth
                onClick={handleFormClick}
            >
                <Typography
                    variant="body1"
                    className={clsx(classes.label, {
                        [classes.labelFocused]: focus,
                        [classes.labelTransformed]: (focus || value.length > 0 || chips.length > 0)
                        })}
                >
                    {label}
                </Typography>
                {printChips(elements)}
                <Input
                    inputRef={input}
                    disableUnderline
                    autoFocus={autoFocus}
                    onFocus={() => setFocus(true)}
                    onBlur={handleBlur}
                    value={value}
                    onChange={handleChange}
                    onKeyDown={handleKey}
                />
            </FormControl>
            <Divider
                className={clsx(classes.divider, {
                    [classes.dividerFocused]: focus
                })}
            />
        </div>
    );
};

MultipleElementsInput.propTypes = {
    classes: PropTypes.object,
    elements: PropTypes.array,
    label: PropTypes.string,
    autoFocus: PropTypes.bool,
    onChange: PropTypes.func,
    inputValidation: PropTypes.func.isRequired
};

export default withStyles(useStyles)(MultipleElementsInput);
